/**
 * Transaction Mode taxonomy (Phase 20).
 *
 * "Mode" describes HOW a transaction was made — orthogonal to the merchant
 * category (e.g. a "Dining" transaction can be an "Online" mode order via
 * Swiggy OR an "Offline / POS" tap at the restaurant). Before Phase 20 we
 * conflated the two, treating "Online" as a category which broke reward
 * calculations.
 *
 * The user-facing Mode selector exposes ONE label (the array below). On
 * submission, `decomposeMode(label)` splits it into the three storage
 * fields the backend expects:
 *
 *   { mode: 'online' | 'offline',   // used by rules engine for rule matching
 *     is_emi: bool,                  // orthogonal flag
 *     is_international: bool         // orthogonal flag
 *   }
 *
 * The rendering helper `formatMode(...)` does the reverse — useful for
 * rendering an existing transaction's mode in a list or detail view.
 */

/**
 * The five mode options offered to users in the Mode selector. Ordered by
 * frequency-of-use (Online first because >70% of consumer credit-card spend
 * in India is online).
 */
export const TXN_MODES = [
  {
    value: 'online',
    label: 'Online',
    description: 'E-commerce, online payment, app or web checkout.',
  },
  {
    value: 'offline',
    label: 'Offline / POS',
    description: 'Card swiped or tapped at a physical point of sale.',
  },
  {
    value: 'emi',
    label: 'EMI',
    description: 'Converted to an equated monthly instalment.',
  },
  {
    value: 'international',
    label: 'International',
    description: 'Foreign-currency transaction or overseas merchant.',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Anything else — wallets, recurring debits, etc.',
  },
];

export const DEFAULT_MODE_VALUE = 'online';

/**
 * Decompose a UI mode value (one of TXN_MODES[*].value) into the three
 * storage fields the backend expects on a transaction.
 */
export const decomposeMode = (modeValue) => {
  switch (modeValue) {
    case 'offline':
      return { mode: 'offline', is_emi: false, is_international: false };
    case 'emi':
      // EMIs are conventionally offline-funded (the user already swiped the
      // card before electing the EMI conversion). The rules engine treats
      // EMIs as `mode='offline'` for cap-matching purposes.
      return { mode: 'offline', is_emi: true, is_international: false };
    case 'international':
      // Default to online — most international charges are e-commerce. If
      // the user explicitly wants an offline-international charge they can
      // edit the row after submission (rare edge case).
      return { mode: 'online', is_emi: false, is_international: true };
    case 'other':
      return { mode: 'online', is_emi: false, is_international: false };
    case 'online':
    default:
      return { mode: 'online', is_emi: false, is_international: false };
  }
};

/**
 * Reverse of `decomposeMode` — pick a sensible UI label given a transaction's
 * storage triple. Use this when rendering an existing transaction.
 *
 * Precedence (since the labels aren't perfectly orthogonal):
 *   1. EMI         (is_emi=true regardless of online/offline)
 *   2. International (is_international=true regardless of online/offline)
 *   3. Offline / POS (mode='offline')
 *   4. Online       (mode='online' — default)
 */
export const composeModeValue = ({ mode, is_emi, is_international } = {}) => {
  if (is_emi) return 'emi';
  if (is_international) return 'international';
  if (mode === 'offline') return 'offline';
  return 'online';
};

/** Pretty-print a transaction's mode as a single label for badges. */
export const formatMode = (txn) => {
  const v = composeModeValue(txn || {});
  const entry = TXN_MODES.find((m) => m.value === v);
  return entry?.label || 'Online';
};
